import {
    FilterLogic,
    FilterOperator,
    type ILegalListFilter,
    type ILegalListFilterRow,
    type TLegalCompaniesBlackListByKey,
    YandexListTypes,
} from "entities/template";
import type { ILegalOrgBankDetail, ILegalOrgListItem } from "entities/legal_orgs";

export type CompanyListScopeMode = "bg" | "bu" | "country";

export function filterOrgsByScopeKey(
    allOrgs: ILegalOrgListItem[],
    scope: CompanyListScopeMode,
    scopeKey: string
): ILegalOrgListItem[] {
    switch (scope) {
        case "bg":
            return allOrgs.filter((o) => String(o.ya_bg_id) === String(scopeKey));
        case "bu":
            return allOrgs.filter((o) => String(o.ya_bu_id) === String(scopeKey));
        case "country":
            return allOrgs.filter(
                (o) => o.ya_country_code.toUpperCase() === scopeKey.toUpperCase()
            );
        default:
            return [];
    }
}

export function getScopeCompanyCounts(
    allOrgs: ILegalOrgListItem[],
    scope: CompanyListScopeMode,
    scopeKey: string,
    excludedYaIds: string[] | undefined
): { total: number; selected: number } {
    const inScope = filterOrgsByScopeKey(allOrgs, scope, scopeKey);
    const total = inScope.length;
    const bl = new Set(excludedYaIds ?? []);
    const selected = inScope.filter((o) => !bl.has(o.ya_id)).length;
    return { total, selected };
}

export function orgBelongsToScopeSelection(
    org: ILegalOrgListItem,
    optionsList: YandexListTypes,
    scopeList: string[]
): boolean {
    if (!scopeList.length) return false;
    const set = new Set(scopeList.map((s) => String(s)));
    switch (optionsList) {
        case YandexListTypes.yaBGs:
            return set.has(String(org.ya_bg_id));
        case YandexListTypes.yaBUs:
            return set.has(String(org.ya_bu_id));
        case YandexListTypes.yaCountries:
            return Array.from(set).some(
                (code) => org.ya_country_code.toUpperCase() === String(code).toUpperCase()
            );
        default:
            return false;
    }
}

export function collectYaIdsInScopeSelection(
    allOrgs: ILegalOrgListItem[],
    optionsList: YandexListTypes,
    scopeList: string[]
): Set<string> {
    const ids = new Set<string>();
    for (const o of allOrgs) {
        if (orgBelongsToScopeSelection(o, optionsList, scopeList)) ids.add(o.ya_id);
    }
    return ids;
}


export function buildAdditionalCompaniesOptionOrgs(
    allOrgs: ILegalOrgListItem[],
    optionsList: YandexListTypes,
    scopeList: string[],
    selectedAdditionalIds: string[]
): ILegalOrgListItem[] {
    const inScopeIds = collectYaIdsInScopeSelection(allOrgs, optionsList, scopeList);
    const pool = allOrgs.filter((o) => !inScopeIds.has(o.ya_id));
    const poolIds = new Set(pool.map((o) => o.ya_id));
    const selectedSet = new Set(selectedAdditionalIds);
    const extras = allOrgs.filter((o) => selectedSet.has(o.ya_id) && !poolIds.has(o.ya_id));
    const merged = [...pool, ...extras];
    merged.sort((a, b) => a.ya_name.localeCompare(b.ya_name));
    return merged;
}

export const LEGAL_YANDEX_DEFAULT_ORG_NAME = 'ООО "ЯНДЕКС"';

function getCountriesBlackListEntry(
    countriesBlackList: TLegalCompaniesBlackListByKey | undefined,
    countryCode: string
): string[] | undefined {
    if (!countriesBlackList) return undefined;
    const code = countryCode.toUpperCase();
    for (const [k, v] of Object.entries(countriesBlackList)) {
        if (String(k).toUpperCase() === code) return v;
    }
    return undefined;
}

export function isOrgExcludedByScopeBlackList(
    org: ILegalOrgListItem,
    optionsList: YandexListTypes,
    bgBlackList?: TLegalCompaniesBlackListByKey,
    buBlackList?: TLegalCompaniesBlackListByKey,
    countriesBlackList?: TLegalCompaniesBlackListByKey
): boolean {
    switch (optionsList) {
        case YandexListTypes.yaBGs:
            return (bgBlackList?.[String(org.ya_bg_id)] ?? []).includes(org.ya_id);
        case YandexListTypes.yaBUs:
            return (buBlackList?.[String(org.ya_bu_id)] ?? []).includes(org.ya_id);
        case YandexListTypes.yaCountries:
            return (getCountriesBlackListEntry(countriesBlackList, org.ya_country_code) ?? []).includes(org.ya_id);
        default:
            return false;
    }
}

function rowMatchesBankDetailField(row: ILegalListFilterRow, detail: Record<string, string>): boolean {
    const fieldVal = String(detail[row.field]?.toLowerCase() ?? "");
    const needle = row.value?.toLowerCase() ?? "";
    switch (row.operator) {
        case FilterOperator.equals:
            return fieldVal === needle;
        case FilterOperator.contains:
            return fieldVal.includes(needle);
        case FilterOperator.notEquals:
            return fieldVal !== needle;
        case FilterOperator.notContains:
            return !fieldVal.includes(needle);
        case FilterOperator.empty:
            return fieldVal.trim() === "";
        case FilterOperator.notEmpty:
            return fieldVal.trim() !== "";
        default:
            return true;
    }
}

function isBankDetailsFilterRowActive(row: ILegalListFilterRow): boolean {
    if (row.operator === FilterOperator.empty || row.operator === FilterOperator.notEmpty) {
        return true;
    }
    return (row.value ?? "").trim() !== "";
}

export function orgMatchesBankDetailsFilter(
    bankDetails: ILegalOrgBankDetail[] | undefined | null,
    filter: ILegalListFilter | undefined
): boolean {
    if (!filter?.rows?.length) return true;
    const activeRows = filter.rows.filter(isBankDetailsFilterRowActive);
    if (activeRows.length === 0) return true;
    if (!bankDetails?.length) return true;

    const evalDetail = (detail: ILegalOrgBankDetail) => {
        const flat: Record<string, string> = {};
        for (const [k, v] of Object.entries(detail)) {
            if (v === undefined || v === null) continue;
            flat[k] = String(v);
        }
        const results = activeRows.map((row) => rowMatchesBankDetailField(row, flat));
        return filter.logic === FilterLogic.or ? results.some(Boolean) : results.every(Boolean);
    };

    return bankDetails.some(evalDetail);
}

export function filterBankDetailsByListFilter(
    bankDetails: ILegalOrgBankDetail[] | undefined | null,
    filter: ILegalListFilter | undefined
): ILegalOrgBankDetail[] {
    const source = bankDetails ?? [];
    if (!filter?.rows?.length) return source;
    const activeRows = filter.rows.filter(isBankDetailsFilterRowActive);
    if (activeRows.length === 0) return source;

    return source.filter((detail) => {
        const flat: Record<string, string> = {};
        for (const [k, v] of Object.entries(detail)) {
            if (v === undefined || v === null) continue;
            flat[k] = String(v);
        }
        const results = activeRows.map((row) => rowMatchesBankDetailField(row, flat));
        return filter.logic === FilterLogic.or ? results.some(Boolean) : results.every(Boolean);
    });
}

export interface ILegalYandexDocumentOrgSelectionConfig {
    optionsList: YandexListTypes;
    list: string[] | undefined;
    bgBlackList?: TLegalCompaniesBlackListByKey;
    buBlackList?: TLegalCompaniesBlackListByKey;
    countriesBlackList?: TLegalCompaniesBlackListByKey;
    additionalCompanies?: string[];
    bankDetailsFilter?: ILegalListFilter;
}

/** Порядок `component.list` (ya_id); в конец — то, чего не было в list (например fallback). */
function orderOrgsByComponentListIds(orgs: ILegalOrgListItem[], listIds: string[]): ILegalOrgListItem[] {
    const byId = new Map(orgs.map((o) => [String(o.ya_id), o]));
    const ordered: ILegalOrgListItem[] = [];
    const used = new Set<string>();
    for (const raw of listIds) {
        const id = String(raw);
        const o = byId.get(id);
        if (o) {
            ordered.push(o);
            used.add(id);
        }
    }
    for (const o of orgs) {
        const id = String(o.ya_id);
        if (!used.has(id)) {
            ordered.push(o);
            used.add(id);
        }
    }
    return ordered;
}

function appendDefaultOrgIfEmpty(
    allOrgs: ILegalOrgListItem[],
    byId: Map<string, ILegalOrgListItem>
): void {
    if (byId.size > 0) return;
    const def = allOrgs.find((o) => o.ya_name === LEGAL_YANDEX_DEFAULT_ORG_NAME);
    if (def) byId.set(String(def.ya_id), def);
}

export function collectSelectableOrgsForLegalYandexDocument(
    allOrgs: ILegalOrgListItem[],
    cfg: ILegalYandexDocumentOrgSelectionConfig,
    getBankDetailsForOrg?: (yaId: string) => ILegalOrgBankDetail[] | undefined
): ILegalOrgListItem[] {
    const {
        optionsList,
        list = [],
        bgBlackList,
        buBlackList,
        countriesBlackList,
        additionalCompanies,
        bankDetailsFilter,
    } = cfg;

    const byId = new Map<string, ILegalOrgListItem>();

    switch (optionsList) {
        case YandexListTypes.yaOrgs: {
            const idSet = new Set(list.map(String));
            for (const o of allOrgs) {
                if (idSet.has(String(o.ya_id))) {
                    byId.set(String(o.ya_id), o);
                }
            }
            if (list.length === 0) {
                appendDefaultOrgIfEmpty(allOrgs, byId);
            }
            break;
        }
        case YandexListTypes.yaBGs:
        case YandexListTypes.yaBUs:
        case YandexListTypes.yaCountries: {
            for (const o of allOrgs) {
                if (!orgBelongsToScopeSelection(o, optionsList, list)) continue;
                if (isOrgExcludedByScopeBlackList(o, optionsList, bgBlackList, buBlackList, countriesBlackList)) {
                    continue;
                }
                byId.set(String(o.ya_id), o);
            }
            for (const id of additionalCompanies ?? []) {
                const o = allOrgs.find((x) => String(x.ya_id) === String(id));
                if (o) byId.set(String(o.ya_id), o);
            }
            if (list.length === 0 && (additionalCompanies?.length ?? 0) === 0) {
                appendDefaultOrgIfEmpty(allOrgs, byId);
            }
            break;
        }
        default: {
            for (const o of allOrgs) {
                byId.set(String(o.ya_id), o);
            }
            break;
        }
    }

    let result = Array.from(byId.values());
    if (getBankDetailsForOrg && bankDetailsFilter) {
        result = result.filter((o) =>
            orgMatchesBankDetailsFilter(getBankDetailsForOrg(o.ya_id), bankDetailsFilter)
        );
    }

    switch (optionsList) {
        case YandexListTypes.yaOrgs:
            return orderOrgsByComponentListIds(result, list);
        default:
            result.sort((a, b) => a.ya_name.localeCompare(b.ya_name));
            return result;
    }
}
